import {
  Card,
  CardMedia,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import {
  DATE_FORMAT,
  WHITELABEL_IMAGE_PLACEHOLDER
} from 'components/constants/Constants';
import { Controller, useFormContext } from 'react-hook-form';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { CartItem, CartState } from '../../../contexts/cart/types';
import { ProductType } from 'types/Products';
import dayjs from 'dayjs';

type CartRowProps = CartItem & {
  onDelete: () => void;
  index: number;
};

export const CartRow: React.FC<CartRowProps> = props => {
  const {
    register,
    control,
    formState: { errors }
  } = useFormContext<CartState>();
  return (
    <Card
      key={props.old_key}
      sx={theme => ({
        p: 1,
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        gap: 1,
        justifyContent: 'space-between',
        alignItems: 'center',
        [theme.breakpoints.down('md')]: {
          alignItems: 'flex-start'
        }
      })}
      variant="outlined"
    >
      <Stack
        sx={theme => ({
          flexGrow: 1,
          justifyContent: 'space-between',
          [theme.breakpoints.down('md')]: {
            flexWrap: 'wrap',
            gap: 4
          }
        })}
        direction="row"
        gap={4}
        alignItems="center"
      >
        <Stack direction="row" gap={2} alignItems="center">
          <CardMedia
            component="img"
            sx={{ objectFit: 'cover', width: 75, height: 75 }}
            src={props.imageUrl ?? WHITELABEL_IMAGE_PLACEHOLDER}
          />
          <Link
            sx={{ minWidth: '140px' }}
            component={RouterLink}
            to={`/${
              props.type === ProductType.FORMULATIONS
                ? 'concepts'
                : props.type.toLowerCase()
            }/permalink/${props.old_key}`}
          >
            {props.name}
          </Link>
        </Stack>
        <Stack
          direction="row"
          gap={1}
          justifySelf="right"
          flexGrow={1}
          justifyContent={'flex-end'}
          width={'100%'}
          sx={theme => ({
            [theme.breakpoints.down('md')]: {
              justifyContent: 'flex-start'
            },
            [theme.breakpoints.down('sm')]: {
              flexWrap: 'wrap',
              flexDirection: 'column',
              gap: 2
            }
          })}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Controller
              name={`items.${props.index}.neededBy`}
              control={control}
              render={({ field: { onChange, value } }) => (
                <DatePicker
                  slotProps={{
                    textField: {
                      size: 'small',
                      InputLabelProps: {
                        shrink: true
                      },
                      fullWidth: true
                    },
                    field: {
                      clearable: true
                    }
                  }}
                  format={DATE_FORMAT}
                  minDate={dayjs()}
                  label="Needed by"
                  {...register(`items.${props.index}.neededBy`)}
                  value={value ? dayjs(value) : null}
                  onChange={value =>
                    onChange(value ? value.startOf('day').toDate() : null)
                  }
                />
              )}
            />
          </LocalizationProvider>
          <TextField
            required
            size="small"
            label="Number of Samples"
            type="number"
            fullWidth
            {...register(`items.${props.index}.quantity`, {
              required: 'Quantity is required',
              min: { value: 1, message: 'Must be greater than zero' }
            })}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">units</InputAdornment>
                )
              },
              inputLabel: { shrink: true }
            }}
            error={!!errors.items?.[props.index]?.quantity}
            helperText={errors.items?.[props.index]?.quantity?.message}
          />
          <TextField
            size="small"
            label="Estimated Yearly Usage"
            type="number"
            fullWidth
            {...register(`items.${props.index}.estimatedYearlyUsage`, {
              required: false,
              min: { value: 1, message: 'Must be greater than zero' }
            })}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">kg</InputAdornment>
              },
              inputLabel: { shrink: true }
            }}
            error={!!errors.items?.[props.index]?.estimatedYearlyUsage}
            helperText={
              errors.items?.[props.index]?.estimatedYearlyUsage?.message
            }
          />
        </Stack>
      </Stack>
      <IconButton onClick={props.onDelete} color="primary">
        <DeleteIcon />
      </IconButton>
    </Card>
  );
};
